import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconGolf, IconSearch, IconSun, IconCloud, IconCloudRain, IconSnowflake, IconWind, IconCloudStorm,
  IconChevronLeft, IconChevronRight, IconPhoto,
} from '@tabler/icons-react';
import api from '../api/api';
import ScoreCardContent from './ScoreCardContent';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

const WEATHER_OPTIONS = [
  { code: 'sunny',  label: '맑음',   Icon: IconSun,        color: '#f59e0b' },
  { code: 'cloudy', label: '흐림',   Icon: IconCloud,      color: '#6b7280' },
  { code: 'rain',   label: '비',     Icon: IconCloudRain,  color: '#3b82f6' },
  { code: 'snow',   label: '눈',     Icon: IconSnowflake,  color: '#60a5fa' },
  { code: 'wind',   label: '바람',   Icon: IconWind,       color: '#8b5cf6' },
  { code: 'storm',  label: '폭풍',   Icon: IconCloudStorm, color: '#ef4444' },
];

const WeatherIcon = ({ code, size = 16 }) => {
  const w = WEATHER_OPTIONS.find(o => o.code === code);
  if (!w) return null;
  return <w.Icon size={size} color={w.color} title={w.label} />;
};

const STATUS_MAP = {
  PENDING:   { label: '대기',   color: 'bg-yellow-lt' },
  CANCELLED: { label: '취소',   color: 'bg-red-lt'    },
  COMPLETED: { label: '완료',   color: 'bg-blue-lt'   },
};

export default function GolfReservationsPage({ me, showToast }) {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [courses, setCourses] = useState([]);

  // 필터
  const [courseId, setCourseId] = useState('');
  const [status, setStatus] = useState('');
  const [filterGameType, setFilterGameType] = useState('');
  const [filterUseYn, setFilterUseYn] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');

  // 정렬
  const [sortField, setSortField] = useState('reservedDate');
  const [sortDir, setSortDir] = useState('desc');

  // 스코어카드 모달
  const [scoreReservation, setScoreReservation] = useState(null);
  const [scoreData, setScoreData] = useState([]);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreSaving, setScoreSaving] = useState(false);

  useEffect(() => {
    api.get('/golf/courses').then(res => {
      setCourses(res.data?.content || res.data || []);
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: 20, sortField, sortDir };
      if (courseId) params.courseId = courseId;
      if (status) params.status = status;
      if (keyword) params.keyword = keyword;
      if (filterGameType) params.gameType = filterGameType;
      if (filterUseYn) params.useYn = filterUseYn;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await api.get('/golf/reservations', { params });
      setReservations(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalElements(res.data.totalElements || 0);
    } catch {
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [page, courseId, status, keyword, filterGameType, filterUseYn, dateFrom, dateTo, sortField, sortDir]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setKeyword(searchInput);
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: '#206bc4' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const displayName = (r) => r.reservedUserName || r.reserverName || '-';

  const openScoreCard = async (r) => {
    // 모바일: 페이지 이동
    if (window.innerWidth < 768) {
      navigate(`/golf/reservations/${r.id}/scorecard`, { state: { reservation: r } });
      return;
    }
    // PC: 모달
    setScoreReservation(r);
    setScoreLoading(true);
    try {
      const res = await api.get(`/golf/reservations/${r.id}/scores`);
      setScoreData(res.data || []);
    } catch {
      setScoreData([]);
    } finally {
      setScoreLoading(false);
    }
  };

  const handleDeleteReservation = async () => {
    if (!scoreReservation) return;
    try {
      await api.delete(`/golf/reservations/${scoreReservation.id}`);
      setReservations(prev => prev.filter(r => r.id !== scoreReservation.id));
      setScoreReservation(null);
      showToast('예약이 삭제되었습니다.');
    } catch {
      showToast('삭제에 실패했습니다.', 'danger');
    }
  };

  const handlePhotosChange = (photos) => {
    if (!scoreReservation) return;
    setReservations(prev => prev.map(r => r.id === scoreReservation.id ? { ...r, photos } : r));
    setScoreReservation(prev => (prev ? { ...prev, photos } : prev));
  };

  const handleSaveScores = async (scores) => {
    if (!scoreReservation) return;
    setScoreSaving(true);
    try {
      const res = await api.put(`/golf/reservations/${scoreReservation.id}/scores`, scores);
      setScoreData(res.data || []);
      showToast('스코어가 저장되었습니다.');
    } catch {
      showToast('스코어 저장에 실패했습니다.', 'danger');
    } finally {
      setScoreSaving(false);
    }
  };

  return (
    <>
      {/* 헤더 */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <IconGolf size={22} />
          <h4 className="mb-0">골프 예약 관리</h4>
          <span className="badge bg-blue-lt ms-1">{totalElements.toLocaleString()}건</span>
        </div>
      </div>

      {/* 필터 */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <form className="row g-2 align-items-end flex-wrap" onSubmit={handleSearch}>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={courseId}
                onChange={e => { setCourseId(e.target.value); setPage(0); }}>
                <option value="">전체 골프장</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={status}
                onChange={e => { setStatus(e.target.value); setPage(0); }}>
                <option value="">전체 상태</option>
                {Object.entries(STATUS_MAP).map(([k, v]) =>
                  <option key={k} value={k}>{v.label}</option>
                )}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={filterGameType}
                onChange={e => { setFilterGameType(e.target.value); setPage(0); }}>
                <option value="">경기유형</option>
                <option value="일반">일반</option>
                <option value="메이저">메이저</option>
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={filterUseYn}
                onChange={e => { setFilterUseYn(e.target.value); setPage(0); }}>
                <option value="">유효</option>
                <option value="Y">Y</option>
                <option value="N">N</option>
              </select>
            </div>
            <div className="col-auto">
              <input type="date" className="form-control form-control-sm" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(0); }} />
            </div>
            <div className="col-auto" style={{ lineHeight: '32px', color: '#aaa' }}>~</div>
            <div className="col-auto">
              <input type="date" className="form-control form-control-sm" value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(0); }} />
            </div>
            <div className="col-auto">
              <div className="input-group input-group-sm">
                <input type="text" className="form-control form-control-sm" style={{ width: 150 }}
                  placeholder="예약자명, 연락처"
                  value={searchInput} onChange={e => setSearchInput(e.target.value)} />
                <button className="btn btn-sm btn-primary" type="submit"><IconSearch size={14} /></button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 테이블 */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('reservedDate')}>
                  예약일시<SortIndicator field="reservedDate" />
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('golfCourse.name')}>
                  골프장<SortIndicator field="golfCourse.name" />
                </th>
                <th>코스</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('reserverName')}>
                  예약자<SortIndicator field="reserverName" />
                </th>
                <th>연락처</th>
                <th style={{ cursor: 'pointer', userSelect: 'none', width: 60 }} onClick={() => handleSort('playerCount')}>
                  인원<SortIndicator field="playerCount" />
                </th>
                <th style={{ width: 50 }}>홀</th>
                <th style={{ cursor: 'pointer', userSelect: 'none', width: 80 }} onClick={() => handleSort('greenFee')}>
                  그린피<SortIndicator field="greenFee" />
                </th>
                <th style={{ width: 70 }}>경기유형</th>
                <th style={{ width: 55 }}>유효</th>
                <th style={{ width: 80 }}>상태</th>
                <th className="text-center" style={{ width: 44 }}>사진</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-4 text-muted">
                  <span className="spinner-border spinner-border-sm me-2" />불러오는 중...
                </td></tr>
              ) : reservations.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-4 text-muted">예약 내역이 없습니다.</td></tr>
              ) : reservations.map(r => {
                const st = STATUS_MAP[r.status] || { label: r.status, color: '' };
                return (
                  <tr key={r.id}>
                    <td className="fw-medium" style={{ fontSize: 13, cursor: r.status === 'COMPLETED' ? 'pointer' : 'default' }}
                      onClick={() => r.status === 'COMPLETED' && openScoreCard(r)}
                      title={r.status === 'COMPLETED' ? '스코어카드 보기' : undefined}>
                      {r.status === 'COMPLETED' ? (
                        <span className="text-primary" style={{ textDecoration: 'underline', textUnderlineOffset: 2 }}>
                          {r.reservedDate}
                        </span>
                      ) : (
                        <span>{r.reservedDate}</span>
                      )}<br />
                      <span className="d-inline-flex align-items-center gap-1 text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        <span>{r.reservedTime?.slice(0, 5)}</span>
                        {r.weather && <WeatherIcon code={r.weather} size={12} />}
                        {r.temperature && <span style={{ fontSize: 11 }}>{r.temperature}°</span>}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{r.golfCourseName}</td>
                    <td className="text-muted" style={{ fontSize: 12 }}>
                      {r.sections?.map(s => s.sectionName).join(' + ') || '-'}
                    </td>
                    <td style={{ fontSize: 13 }}>{displayName(r)}</td>
                    <td className="text-muted" style={{ fontSize: 13 }}>{r.reserverTel || '-'}</td>
                    <td className="text-muted" style={{ fontSize: 13 }}>{r.playerCount}명</td>
                    <td className="text-muted" style={{ fontSize: 13 }}>{r.totalHoles ? `${r.totalHoles}홀` : '-'}</td>
                    <td className="text-muted" style={{ fontSize: 13 }}>
                      {r.greenFee != null ? r.greenFee : '-'}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {r.gameType
                        ? <span className={`badge ${r.gameType === '메이저' ? 'bg-purple-lt' : 'bg-azure-lt'}`}>{r.gameType}</span>
                        : <span className="text-muted">-</span>}
                      {r.memo && <span className="ms-1 text-muted" style={{ fontSize: 11 }}>{r.memo}</span>}
                    </td>
                    <td className="text-center">
                      {r.useYn === 'N'
                        ? <span className="badge bg-red-lt">N</span>
                        : <span className="badge bg-green-lt">Y</span>}
                    </td>
                    <td><span className={`badge ${st.color}`}>{st.label}</span></td>
                    <td className="text-center">
                      {r.photos?.length > 0
                        ? <IconPhoto size={16} color="#206bc4" title={`사진 ${r.photos.length}장`} />
                        : <span className="text-muted">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="card-footer d-flex justify-content-center py-2">
            <ul className="pagination pagination-sm m-0">
              <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => Math.max(0, p - 1))}>
                  <IconChevronLeft size={14} />
                </button>
              </li>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                const start = Math.max(0, Math.min(page - 4, totalPages - 10));
                const pg = start + i;
                if (pg >= totalPages) return null;
                return (
                  <li key={pg} className={`page-item ${pg === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(pg)}>{pg + 1}</button>
                  </li>
                );
              })}
              <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>
                  <IconChevronRight size={14} />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
      {/* 스코어카드 모달 */}
      {scoreReservation && (
        <ScoreCardModal
          reservation={scoreReservation}
          scores={scoreData}
          loading={scoreLoading}
          saving={scoreSaving}
          onClose={() => setScoreReservation(null)}
          onSave={handleSaveScores}
          onDelete={handleDeleteReservation}
          onPhotosChange={handlePhotosChange}
        />
      )}
    </>
  );
}

// ── ScoreCardModal (PC용 모달 래퍼) ──────────────────────────────────────
function ScoreCardModal({ reservation, scores, loading, saving, onClose, onSave, onDelete, onPhotosChange }) {
  return (
    <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}
      onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, onClose)}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 920 }}>
        <div className="modal-content" style={{ borderRadius: 12, overflow: 'hidden' }}>
          <ScoreCardContent
            reservation={reservation}
            scores={scores}
            loading={loading}
            saving={saving}
            onSave={onSave}
            onDelete={onDelete}
            onPhotosChange={onPhotosChange}
          />
          <div className="modal-footer py-2">
            <button className="btn btn-sm" onClick={onClose}>닫기</button>
          </div>
        </div>
      </div>
    </div>
  );
}
